import React, { useEffect, useState } from 'react';
import gql from 'graphql-tag';
import { connect } from 'react-redux';
import idx from 'idx';
import { css } from 'emotion';
import { sortBy } from 'lodash';
import PJBrandbar from '@peachjar/ui/dist/lib/components/PageLayouts/Brandbar'
import config from '../../../../src/config';
import CreditsMenu from '../CreditsMenu';
import ProfileDropdown from '../ProfileDropdown';
import Settingsdropdown from '../SettingsDropdown';
import { SodUser, ForEntity, Role, Entity as SodEntity } from '../../../_api/SodUser';
import { LinkedAccount } from '../../../_api/Profile';
import {
  getRolesFromPermissions,
} from '../../components/RoleCheck';
import RolesEnum from '../../RolesEnum';
import { Flags } from '../../flagging/flags';
import useAccountContext, {
  AccountContextApi,
} from '../../../hooks/useAccountContext';
import apolloClient from '../../apollo/portalBFF.apolloClient';
import FlyerBoardDropDown from '../FlyerBoardDropdown';

const { DISTRICT_ADMIN, SCHOOL_ADMIN, ORG } = RolesEnum;
const { DASHBOARD_URL } = config;

type Props = {
  sodUser?: SodUser;
  processMode: boolean;
  flags: Flags;
  pathname: string;
  apolloError: any
};


type Entity = {
  id: number;
  name: string;
  type: string;
}

const GET_SCHOOLS_BY_SCHOOL_ID = gql`
  query getSchoolsBySchoolId($schoolIds: [Int!]!) {
    getSchoolsBySchoolId(schoolIds: $schoolIds){
      items {
        schoolId
        name
      }
    }
  }
`;

export const GET_DISTRICT_NAME_BY_ID = gql`
  query getDistrict($input: V2_DistrictIdsInput) {
    getDistrict(input: $input){
      name
    }
  }
`

export const GET_ROLES_FOR_USER = gql`
  query getRolesForUser($input: V2_GetRolesForUserInput) {
    getRolesForUser(input: $input){
      forEntity {
        entityType
        entityId
      }
    }
  }
`;


export function getTopRoleEntity(roles: Role[]): SodEntity | undefined {
  const isAdmin = r => (idx(r, _ => _.roles) || []).includes('admin')
  const isDistrict = r => idx(r, _ => _.forEntity.entityType) === 'district'

  const topResult = sortBy(roles.filter(isAdmin), r => !isDistrict(r))[0]
  const result = idx(topResult, _ => _.forEntity) as SodEntity

  return result
}

const Brandbar = ({ sodUser, processMode, flags, pathname, apolloError }: Props) => {
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccount[]>([])
  const {
    profile: _profileAccount
  } = useAccountContext() as AccountContextApi;

  useEffect(() => {
    const getLinkedAccounts = async (accounts: LinkedAccount[]) => {
      const linkedAccountList: LinkedAccount[] = [];
      for (let i = 0; i < accounts.length; i++) {
        let account = accounts[i];

        if (account.scopes.includes("staff") || account.scopes.includes("volunteer")) {
          const entities = await getRolesForUser(account.userId)
          const entity = await getEntity(entities)
          if (entity) {
            linkedAccountList.push({
              ...account,
              hierarchy: {
                id: entity.id.toString(),
                name: `${entity.name}`,
                type: entity.type
              }
            })
            continue
          }
        }
        linkedAccountList.push(account)
      }
      setLinkedAccounts(linkedAccountList)
    }

    const linkedAccounts = idx(_profileAccount, _ => _.linkedAccounts) || [];
    if (linkedAccounts.length > 0) {
      getLinkedAccounts(linkedAccounts)
    }

  }, [_profileAccount])

  const getRolesForUser = async (userIdParam: number): Promise<ForEntity[]> => {
    const { data: { getRolesForUser } } = await apolloClient.query({
      query: GET_ROLES_FOR_USER,
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
      variables: {
        input: {
          userId: userIdParam.toString()
        }
      }
    })

    if (getRolesForUser && getRolesForUser.length > 0) {
      return getRolesForUser;
    }
    return []
  }

  const getEntity = async (entities: ForEntity[]): Promise<Entity | null> => {
    if (entities.length > 0) {
      const districtEntities = entities.filter(({ forEntity: { entityType } }) => entityType === 'district')
      let userEntities: ForEntity[] = districtEntities
      if (districtEntities.length === 0) {
        userEntities = entities.filter(({ forEntity: { entityType } }) => entityType === 'school')
        userEntities = userEntities.sort((a, b) => parseInt(a.forEntity.entityId) - parseInt(b.forEntity.entityId))
      }

      const { forEntity } = userEntities[0]
      const entityId = Number(forEntity.entityId)
      let name: string | null = ''
      if (forEntity.entityType === 'district') {
        name = await getDistrictNameById(entityId)
      } else {
        name = await getSchoolNameById(entityId)
      }

      if (name) {
        const moreEntities = forEntity.entityType === 'school' ? entities.length > 1 ? `+more` : '' : ''

        return {
          id: entityId,
          name: `${name} ${moreEntities}`,
          type: forEntity.entityType
        }
      }
    }
    return null
  }

  const getSchoolNameById = async (entityId: number): Promise<string | null> => {
    const { data: { getSchoolsBySchoolId } } = await apolloClient.query({
      query: GET_SCHOOLS_BY_SCHOOL_ID,
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
      variables: {
        schoolIds: entityId
      }
    })

    if (getSchoolsBySchoolId && getSchoolsBySchoolId.items.length > 0) {
      const name = getSchoolsBySchoolId.items[0].name
      return name;
    }

    return null
  }

  const getDistrictNameById = async (districtId: number): Promise<string | null> => {
    const { data: { getDistrict: districtName } } = await apolloClient.query({
      query: GET_DISTRICT_NAME_BY_ID,
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
      variables: {
        input: {
          districtId
        }
      }
    })

    if (districtName) {
      return districtName.name
    }

    return null
  }

  const getUserType = (scopes: string[]): string => {
    if (scopes.includes('parent')) {
      return 'Parent'
    }

    if (scopes.includes('volunteer')) {
      return 'Volunteer'
    }

    if (scopes.includes('staff')) {
      return 'Staff'
    }

    if (scopes.includes('org_member')) {
      return 'Organization'
    }

    return ''
  }
  /**
   * TODO: Move account type calculation to the backend.
   * Note - The method below will break when working with program providers.
   */
  const adminLevel = idx(sodUser, _ => _.permission.adminLevel) || null;
  const approverLevel = idx(sodUser, _ => _.permission.approverLevel) || null;
  const uploaderLevel = idx(sodUser, _ => _.permission.uploaderLevel) || null;
  const hierarchy = idx(sodUser, _ => _.hierarchy)!;
  const scopes = idx(sodUser, _ => _.scopes) || [];
  const isStaffOrVolunteer = scopes.includes('volunteer') || scopes.includes('staff')
  const adminRoles = getRolesFromPermissions(
    approverLevel!,
    adminLevel!,
    uploaderLevel!
  );
  const shouldRenderCreditsMenu = uploaderLevel === 'org';
  const shouldRenderSettingsMenu =
    adminRoles.includes(DISTRICT_ADMIN) ||
    adminRoles.includes(SCHOOL_ADMIN) ||
    adminRoles.includes(ORG);
  const userType = getUserType(scopes)

  const firstName = idx(sodUser, _ => _.firstName) || userType;
  const lastName = idx(sodUser, _ => _.lastName) || '';
  const emailAddress = idx(sodUser, _ => _.email) || 'No email provided.';
  const userId = idx(sodUser, _ => _.id) || '';
  const roles = idx(sodUser, _ => _.roles) || []
  const schoolRolesAdmin = roles.filter((role) =>
    role.forEntity.entityType === 'school' &&
    role.roles.includes('admin')
  )
  const hasMultipleSchoolsAdmin = schoolRolesAdmin.length > 1

  // need to search for top role, entity
  const defaultTopEntity = {
    entityId: hierarchy.id,
    entityType: hierarchy.type,
  };
  const topRoleEntity =
    getTopRoleEntity(roles) || defaultTopEntity;

  // @ts-ignore
  const { portal_nav, org_uploader } = flags;
  const portalNavEnabled = portal_nav === 1;
  const orgUploaderFlagEnabled = org_uploader === 1;

  // const academicGroups = idx(_profileAccount, _ => _.academicGroupMemberships) || [];

  let entityName = '';
  const account = linkedAccounts.find(account => account.userId.toString() === userId.toString())
  if (account) {
    entityName = account.hierarchy.name
  }
  // else {
  //   entityName = academicGroups.length > 0 ? academicGroups[0].groupName : "";
  // }

  return (
    <>
      <PJBrandbar
        logoURI={DASHBOARD_URL}
        className={classNames.brandbar}
        dark={processMode}
      >
        <div
          className={classNames.brandbarContent}
          data-testid="element-brandbar"
        >
          {!processMode && (
            <>
              {orgUploaderFlagEnabled && shouldRenderCreditsMenu && (
                <CreditsMenu />
              )}
              {isStaffOrVolunteer && <FlyerBoardDropDown />}
              {portalNavEnabled && shouldRenderSettingsMenu && (
                <Settingsdropdown
                  roles={adminRoles}
                  hierarchy={hierarchy}
                  topRoleEntity={topRoleEntity}
                  pathname={pathname}
                  hasMultipleSchoolsAdmin={hasMultipleSchoolsAdmin}
                />
              )}
              <ProfileDropdown
                userId={userId}
                entityName={entityName}
                linkedAccounts={linkedAccounts}
                firstName={firstName}
                lastName={lastName}
                emailAddress={emailAddress}
                uploaderLevel={uploaderLevel!}
                pathname={pathname}
                getUserType={getUserType}
                hideProfileLinks={!!apolloError}
              />
            </>
          )}
        </div>
      </PJBrandbar>
    </>
  );
};

Brandbar.fragments = {
  sodUser: gql`
    fragment Brandbar_sodUser on Profile {
      userId
      permission {
        adminLevel
        approverLevel
        uploaderLevel
      }
      firstName
      email
    }
  `,
};

const classNames = {
  brandbar: css`
    z-index: 50;
  `,
  brandbarContent: css`
    flex: 1 1 auto;
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-end;
  `,
  innerPillStyles: css`
    display: flex;
    margin: auto;
  `,
  pillStyles: css`
    display: flex;
    flex-direction: column;
    align-content: center;
    align-self: center;
    font-family: ProximaNova-Regular;
    font-size: 13px;
    font-weight: normal;
    line-height: 16px;
  `,
};

export default connect(
  //@ts-ignore
  state => ({ flags: state.flags }),
  null
)(Brandbar);
